import type { FunctionComponent } from "react";
import { useCallback, useEffect, useState } from "react";

import type { Meta, StoryObj } from "@storybook/react";

import {
  UnencryptedStorageKey,
  useUnencryptedStorage,
} from "@/hooks/useUnencryptedStorage";
import { Button, Text, TextField } from "@/ui/core";
import { Box, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

const Component = () => {
  const storageKey = UnencryptedStorageKey.StorybookExample;
  const {
    deleteUnencryptedValue,
    getUnencryptedStringValue,
    saveUnencryptedStringValue,
  } = useUnencryptedStorage();

  const [newStringValue, setNewStringValue] = useState<string>("");
  const [savedStringValue, setSavedStringValue] = useState<string>("");
  const [didSave, setDidSave] = useState<boolean>(false);
  const [didDelete, setDidDelete] = useState<boolean>(false);

  const saveStoredValue = useCallback(async () => {
    await saveUnencryptedStringValue({
      key: storageKey,
      value: newStringValue,
    });
    setDidSave(false);
  }, [setDidSave, storageKey, saveUnencryptedStringValue, newStringValue]);

  const getStoredValue = useCallback(async () => {
    const storedValue = await getUnencryptedStringValue(storageKey);
    if (!storedValue) return;
    setSavedStringValue(storedValue);
  }, [getUnencryptedStringValue, storageKey, setSavedStringValue]);

  const deleteStoredValue = useCallback(async () => {
    await deleteUnencryptedValue(storageKey);
    const storedValue = await getUnencryptedStringValue(storageKey);
    setSavedStringValue(storedValue || "");
    setDidDelete(false);
  }, [
    deleteUnencryptedValue,
    storageKey,
    getUnencryptedStringValue,
    setDidDelete,
  ]);

  useEffect(() => {
    if (didSave) {
      void saveStoredValue();
    } else {
      void getStoredValue();
    }
  }, [saveStoredValue, getStoredValue, didSave]);

  useEffect(() => {
    if (!didDelete) return;
    void deleteStoredValue();
  }, [deleteStoredValue, didDelete]);

  return (
    <VStack gap={20} width="100%">
      <Text testID={joinTestIDs("storybook", "text")} variant="bodyMedium">
        Save value to storage:
      </Text>
      <TextField
        isAutoCorrect={false}
        onChange={setNewStringValue}
        placeholder="Value to store"
        testID={joinTestIDs("storybook", "textfield")}
        value={newStringValue}
      />
      <Button
        onPress={() => setDidSave(true)}
        testID={joinTestIDs("storybook", "button", "Save")}
        title="Save value"
      />
      <Box alignItems="center">
        <Text testID={joinTestIDs("storybook", "text")} variant="bodyMedium">
          Current value in storage:
        </Text>
        <Text
          testID={joinTestIDs("storybook", "text", "stored")}
          variant="bodyMedium"
        >
          {savedStringValue}
        </Text>
      </Box>
      <Button
        onPress={() => setDidDelete(true)}
        testID={joinTestIDs("storybook", "button", "delete")}
        title="Delete value"
      />
    </VStack>
  );
};

const StoryMeta: Meta<FunctionComponent> = {
  title: "Hooks/UnencryptedStorage",
  component: Component,
};

type Story = StoryObj<FunctionComponent>;

export default StoryMeta;
export const Primary: Story = {};
